import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  Divider,
} from "@mui/material";
import BackButton from "../components/common/BackButton";

interface Step {
  id: number;
  title: string;
  subtitle?: string;
  image?: string;
}

const stepByStepGuide: Step[] = [
  { id: 1, title: "Visit esim.laotel.com" },
  {
    id: 2,
    title: "Select the eSIM package you want",
    image: "guideline/buy1_home.png",
  },
  {
    id: 3,
    title: "Review the package details",
    image: "guideline/buy2_detail.png",
  },
  {
    id: 4,
    title: "Add the package to your cart with the desired quantity",
    subtitle: "You may return to the homepage to add more packages if needed",
  },
  {
    id: 5,
    title: "Tap Confirm in the cart section at the bottom of the screen",
    image: "guideline/buy3_addCart.png",
  },
  {
    id: 6,
    title:
      "Enter your contact details (First Name, Last Name, Phone Number), then proceed",
    subtitle:
      "Complete KYC verification by uploading your passport photo",
      // "Complete KYC verification by uploading your passport photo and a selfie holding your passport",
    image: "guideline/buy4_contact.png",
  },
  {
    id: 7,
    title: "Proceed to the payment page and select a payment method",
    image: "guideline/buy5_payments.png",
  },
  { id: 8, title: "In this example, payment is made via Lao QR" },
  {
    id: 9,
    title: "Tap Generate QR",
    image: "guideline/buy6_laoqr.png",
  },
  {
    id: 10,
    title: "Open your banking app to scan and complete the payment",
    image: "guideline/buy7_Qr_final.png",
  },
  { id: 11, title: "Your purchase is now complete 🎉" },
];

const afterPurchaseSteps: Step[] = [
  {
    id: 1,
    title: "Open the menu and go to My eSIMs",
    image: "guideline/my1_nav.png",
  },
  {
    id: 2,
    title: "You will see the successfully purchased eSIM numbers",
    subtitle: "Select Activate for the eSIM you want to use",
    image: "guideline/my2_inactive.png",
  },
  {
    id: 3,
    title: "Confirm the activation",
    image: "guideline/my3_ok_active.png",
  },
  {
    id: 4,
    title: "The eSIM status will change to Active",
    image: "guideline/my5_activated.png",
  },
  {
    id: 5,
    title:
      "Use your phone to scan the eSIM QR code to install it on your device",
    image: "guideline/my4_esimqr.png",
  },
];

const StepCard = ({ id, title, subtitle, image }: Step) => (
  <Card variant="outlined" sx={{ borderRadius: 2 }}>
    <CardContent sx={{ py: 2.5 }}>
      <Stack spacing={1.5}>
        <Typography
          variant="caption"
          fontWeight={600}
          color="primary"
          sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}
        >
          Step {id}
        </Typography>

        <Typography variant="body1" fontWeight={500}>
          {title}
        </Typography>

        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            – {subtitle}
          </Typography>
        )}

        {image && (
          <Box
            component="img"
            src={image}
            alt={title}
            sx={{
              width: 220,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
            }}
          />
        )}
      </Stack>
    </CardContent>
  </Card>
);

const HowToBuyEsim = () => {
  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Container maxWidth="md">
        {/* Header */}
        <Stack spacing={1.5} mb={4}>
          <BackButton path="/" />
          <Typography variant="h5" fontWeight={600}>
            How to Buy an eSIM
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Buying an eSIM is simple and can be done by yourself—no waiting, no
            advance booking required. You can purchase online anytime, 24/7. The
            price you see is the price you pay, with no extra charges, and your
            eSIM is ready to use immediately after payment.
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Before purchasing, please check device compatibility and complete
            online SIM registration.
          </Typography>
        </Stack>

        {/* Step-by-Step */}
        <Typography variant="subtitle1" fontWeight={600} mb={2}>
          Step-by-Step Guide
        </Typography>

        <Stack spacing={2.5}>
          {stepByStepGuide.map((step) => (
            <StepCard key={step.id} {...step} />
          ))}
        </Stack>

        <Divider sx={{ my: 6 }} />

        {/* After Purchase */}
        <Typography variant="subtitle1" fontWeight={600} mb={2}>
          Where to Find Your eSIM After Purchase
        </Typography>

        <Stack spacing={2.5}>
          {afterPurchaseSteps.map((step) => (
            <StepCard key={`after-${step.id}`} {...step} />
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default HowToBuyEsim;
